import React, { useEffect, useRef } from "react";
import { Chart } from "chart.js/auto";

const impactScores = { low: 1, medium: 2, high: 3 };
const getImpact = (level) => impactScores[level?.toLowerCase?.()] ?? 0;

const LineGraph = ({ data, months }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // Process each data point to calculate RPS and store related findings/improvements
    const combinedDataPoints = data.map((item, index) => {
      // Calculate impact of findings
      const findingImpact = item.relatedFindings.reduce((total, finding) => {
        return total + getImpact(finding?.risk?.level);
      }, 0);

      // Calculate impact of improvements (subtract based on risk level)
      const processedFindings = new Set();
      const improvementImpact = item.relatedImprovements.reduce(
        (total, improvement) => {
          // CRITICAL: Only reduce RPS if the improvement is Done
          if (improvement && improvement.status === "Done") {
            improvement.linked_findings.forEach((findingKey) => {
              // Check if this finding exists in this month's related findings
              const linkedFinding = item.relatedFindings.find(
                (f) => f.key === findingKey,
              );

              // Only reduce RPS if:
              // 1. The improvement is Done
              // 2. The linked finding exists in this month's data
              // 3. We haven't already processed this finding
              if (
                linkedFinding &&
                linkedFinding.risk &&
                !processedFindings.has(findingKey)
              ) {
                total -= getImpact(linkedFinding.risk.level);
                processedFindings.add(findingKey);
              }
            });
          }

          return total;
        },
        0,
      );

      const RPS = findingImpact + improvementImpact;

      // Set the point color
      let pointColor = "#112C22"; // Default color

      return {
        x: months[index],
        y: RPS,
        findings: item.relatedFindings,
        improvements: item.relatedImprovements,
        findingImpact,
        improvementImpact,
        RPS,
        pointBackgroundColor: pointColor,
        pointBorderColor: "#112C22",
      };
    });

    const chartData = {
      labels: months,
      datasets: [
        {
          label: "RPS",
          data: combinedDataPoints,
          fill: true,
          borderColor: "#112C22",
          tension: 0.1,
          pointBackgroundColor: combinedDataPoints.map(
            (point) => point.pointBackgroundColor,
          ),
          pointBorderColor: combinedDataPoints.map(
            (point) => point.pointBorderColor,
          ),
          backgroundColor: "#EBFF99",
          pointRadius: 5,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      clip: false,
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          ticks: {
            stepSize: 1, // only show 0, 1, 2, ...
            precision: 0, // ensure no decimals
          },
          grid: {
            display: true, // keep horizontal lines
            color: "#DEDEDF",
          },
        },
        x: {
          ticks: {
            display: false, // hides the labels
          },
          grid: {
            drawOnChartArea: true,
            color: "#EAECF0", // keep vertical grid lines if needed
          },
          border: {
            display: false, // hide the bottom border
          },
        },
      },
      layout: {
        padding: {
          bottom: 10, // enough space to show the full point
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false, // Disable default tooltip
          external: function (context) {
            // Tooltip Element
            let tooltipEl = document.getElementById("chartjs-tooltip");

            // Create tooltip element if it doesn't exist
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip";
              tooltipEl.style.position = "absolute";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.opacity = 0;
              tooltipEl.style.transition = "opacity 0.3s ease";

              const arrow = document.createElement("div");
              arrow.className = "tooltip-arrow";
              tooltipEl.appendChild(arrow);

              const content = document.createElement("div");
              content.className = "tooltip-content";
              tooltipEl.appendChild(content);

              document.body.appendChild(tooltipEl);
            }

            const tooltipModel = context.tooltip;

            // Hide if no tooltip
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            // Get the data point related to the tooltip
            const dataPoint = context.tooltip.dataPoints[0].raw;
            const monthName = new Date(`${dataPoint.x}-01`).toLocaleString(
              "en-US",
              {
                month: "long",
              },
            );

            // Set text for the tooltip
            let tooltipContent = `<div><strong>${monthName} PRIORITY SCORE: ${dataPoint.RPS}</strong></div>`;

            // Add RPS Calculation Breakdown
            tooltipContent += `<div style="margin-top: 6px; font-size: 12px; font-weight: 500; color: #696969;">Calculation</div>`;
            tooltipContent += `<div>Findings Impact: +${dataPoint.findingImpact}</div>`;
            tooltipContent += `<div>Improvements Impact: ${dataPoint.improvementImpact}</div>`;

            // Parse the current month to filter findings/improvements
            const pointMonth = dataPoint.x; // month label (for example 'Sept')

            // Show findings related to this specific month
            const filteredFindings = dataPoint.findings.filter((finding) => {
              const d = new Date(finding.date);
              const findingMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              return findingMonth === pointMonth;
            });

            if (filteredFindings.length > 0) {
              tooltipContent += `<div style="color: #AD88C6; font-weight: bold">Findings:</div>`;
              filteredFindings.forEach((finding) => {
                const impact =
                  impactScores[
                    finding.risk && finding.risk.level
                      ? finding.risk.level
                      : "null"
                  ];

                // Add colored text for the risk levels
                let levelColor = "#007940"; // Default color for low
                if (finding.risk && finding.risk.level) {
                  if (finding.risk.level === "medium") {
                    levelColor = "#FF7427"; // Orange for medium
                  } else if (finding.risk.level === "high") {
                    levelColor = "#C60C30"; // Red for high
                  }
                }

                const riskLevelText =
                  finding.risk && finding.risk.level
                    ? finding.risk.level
                    : "unknown"; // Default to 'unknown' if level is not available
                tooltipContent += `<div>${finding.key}: +${impact}</div>`;
              });
            }

            const filteredImprovements = dataPoint.improvements.filter(
              (improvement) => {
                const d = new Date(improvement.date);
                const improvementMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                return improvementMonth === pointMonth;
              },
            );

            if (filteredImprovements.length > 0) {
              tooltipContent += `<div style="color: #88AB8E; font-weight: bold">Improvements:</div>`;

              filteredImprovements.forEach((improvement) => {
                if (improvement.status === "Done") {
                  let totalImpact = 0;
                  const processed = new Set();
                  const affectedFindings = [];

                  improvement.linked_findings.forEach((findingKey) => {
                    const finding = dataPoint.findings.find(
                      (f) => f.key === findingKey,
                    );
                    if (finding && finding.risk && !processed.has(findingKey)) {
                      const impact = getImpact(finding.risk.level);
                      totalImpact -= impact;
                      affectedFindings.push({
                        key: findingKey,
                        impact: impact,
                        level: finding.risk.level,
                      });
                      processed.add(findingKey);
                    }
                  });

                  if (totalImpact !== 0) {
                    // Group by risk level to show the breakdown
                    const riskGroups = affectedFindings.reduce(
                      (groups, finding) => {
                        const level = finding.level;
                        if (!groups[level]) {
                          groups[level] = { count: 0, impact: finding.impact };
                        }
                        groups[level].count++;
                        return groups;
                      },
                      {},
                    );

                    // Build the breakdown string
                    const breakdownParts = Object.entries(riskGroups).map(
                      ([level, data]) => {
                        return `-${data.impact} × ${data.count}`;
                      },
                    );

                    tooltipContent += `<div>${improvement.key}: ${breakdownParts.join(" + ")} = ${totalImpact}</div>`;
                  }
                }
              });
            }

            // Set the inner HTML of the tooltip
            const contentEl = tooltipEl.querySelector(".tooltip-content");
            if (contentEl) {
              contentEl.innerHTML = tooltipContent;
            }

            // Set position and styles of tooltip
            const position = context.chart.canvas.getBoundingClientRect();
            tooltipEl.style.opacity = 1;
            tooltipEl.style.left =
              position.left +
              window.pageXOffset +
              tooltipModel.caretX -
              tooltipEl.offsetWidth / 2 +
              "px";

            tooltipEl.style.top =
              position.top +
              window.pageYOffset +
              tooltipModel.caretY +
              20 +
              "px";

            // Apply inline styles to control appearance
            tooltipEl.style.backgroundColor = "white";
            tooltipEl.style.border = "1px solid #DEDEDF";
            tooltipEl.style.borderRadius = "8px";
            tooltipEl.style.boxShadow =
              "0px 3px 3px 0px rgba(17, 44, 34, 0.15)";
            tooltipEl.style.padding = "10px";
            tooltipEl.style.height = "auto";
            tooltipEl.style.overflow = "visible";
            tooltipEl.style.whiteSpace = "normal";
          },
        },
      },
    };

    const ctx = canvasRef.current.getContext("2d");
    chartRef.current = new Chart(ctx, {
      type: "line",
      data: chartData,
      options: chartOptions,
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [data, months]);

  return (
    <div style={{ height: "200px", width: "100%", position: "relative" }}>
      <canvas
        ref={canvasRef}
        style={{ height: "100%", width: "100%" }}
      ></canvas>
    </div>
  );
};

export default LineGraph;
